import { type HTMLAttributes } from 'react';
import { ShieldIcon, BellIcon, StarIcon } from '@bravemove/design-tokens/icons';
import { CheckStatusIcon } from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';

/**
 * F06 / F29 — colored chip identifying an admin role or a team-member role.
 *
 * Admin color mapping:
 *  - super_admin       → accent on accent-ink bg  (shield glyph)
 *  - support           → info tint                (bell glyph)
 *  - finance           → success tint             (check glyph)
 *  - content_moderator → warning tint             (shield glyph)
 *
 * Team-role color mapping (F29, social-lib.jsx L13–45):
 *  - captain   → accent solid bg + accent-ink text  (star glyph)
 *  - cocaptain → accent/15 tint + accent text        (star glyph)
 *  - member    → surface-2 bg + ink-2 text
 *  - invited   → info/10 tint + info text
 *  - pending   → warning/10 tint + warning text
 *  - you       → panel bg + bg text (inverse)
 *
 * The `role` union accepts BOTH surfaces so the same component can be used
 * in the admin dashboard (admin roles) and in team-member lists (team roles).
 * Existing admin-role consumers are unaffected — the type is widened additively.
 *
 * Uses semantic Tailwind utilities only (no raw hex).
 */

// ── Admin roles (F06) ─────────────────────────────────────────────────────
export type AdminRole = 'super_admin' | 'support' | 'finance' | 'content_moderator';

// ── Team roles (F29) ──────────────────────────────────────────────────────
export type TeamRole = 'captain' | 'cocaptain' | 'member' | 'invited' | 'pending' | 'you';

/** Union of admin-role and team-role values. */
export type RoleBadgeRole = AdminRole | TeamRole;

export interface RoleBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  readonly role: RoleBadgeRole;
  readonly size?: 'sm' | 'md';
  /** Override the displayed label (defaults to a humanised version of the role). */
  readonly label?: string;
}

// ── Shared sizing ─────────────────────────────────────────────────────────

const SIZE = {
  sm: { box: 'h-5 ps-1.5 pe-2 text-[9.5px]', icon: 10 },
  md: { box: 'h-6 ps-1.5 pe-2.5 text-[10.5px]', icon: 12 },
};

// ── Admin role config ─────────────────────────────────────────────────────

const ADMIN_ROLE_LABEL: Record<AdminRole, string> = {
  super_admin: 'Super Admin',
  support: 'Support',
  finance: 'Finance',
  content_moderator: 'Content',
};

type AdminIconComponent = typeof ShieldIcon | typeof BellIcon | typeof CheckStatusIcon;

const ADMIN_ROLE_STYLE: Record<
  AdminRole,
  { box: string; iconClass: string; Icon: AdminIconComponent }
> = {
  super_admin: {
    box: 'bg-primary text-primary-ink border border-accent',
    iconClass: 'text-primary-ink',
    Icon: ShieldIcon,
  },
  support: {
    box: 'bg-info/15 text-ink border border-info/30',
    iconClass: 'text-info',
    Icon: BellIcon,
  },
  finance: {
    box: 'bg-success/15 text-ink border border-success/30',
    iconClass: 'text-success',
    Icon: CheckStatusIcon,
  },
  content_moderator: {
    box: 'bg-warning/15 text-ink border border-warning/40',
    iconClass: 'text-warning',
    Icon: ShieldIcon,
  },
};

// ── Team role config ──────────────────────────────────────────────────────

const TEAM_ROLE_LABEL: Record<TeamRole, string> = {
  captain: 'CAPTAIN',
  cocaptain: 'CO-CAPT',
  member: 'MEMBER',
  invited: 'INVITED',
  pending: 'PENDING',
  you: 'YOU',
};

/**
 * Team roles use a square-ish (rounded-sm) pill shape with no icon class
 * because they have a tighter monospace layout vs admin's rounded-full pills.
 * Captain + cocaptain render a star glyph (matches social-lib.jsx L40).
 */
const TEAM_ROLE_STYLE: Record<TeamRole, { box: string; showStar: boolean }> = {
  captain: {
    box: 'bg-primary text-primary-ink',
    showStar: true,
  },
  cocaptain: {
    // ~15% alpha — matches mobile '26' hex suffix convention
    box: 'bg-accent/15 text-ink',
    showStar: true,
  },
  member: {
    box: 'bg-surface-2 text-ink-2',
    showStar: false,
  },
  invited: {
    // ~10% alpha — matches mobile '1A' hex suffix convention
    box: 'bg-info/10 text-ink',
    showStar: false,
  },
  pending: {
    box: 'bg-warning/10 text-ink',
    showStar: false,
  },
  you: {
    // inverse chip: panel bg + bg text
    box: 'bg-panel text-panel-ink',
    showStar: false,
  },
};

// ── Type guards ───────────────────────────────────────────────────────────

function isAdminRole(role: RoleBadgeRole): role is AdminRole {
  return (
    role === 'super_admin' ||
    role === 'support' ||
    role === 'finance' ||
    role === 'content_moderator'
  );
}

// ── Component ─────────────────────────────────────────────────────────────

export function RoleBadge({ role, size = 'md', label, className, ...rest }: RoleBadgeProps) {
  const sz = SIZE[size];

  if (isAdminRole(role)) {
    // ── Admin role path (F06 — unchanged behaviour) ───────────────────────
    const { box, iconClass, Icon } = ADMIN_ROLE_STYLE[role];
    return (
      <span
        role="img"
        aria-label={`Role: ${label ?? ADMIN_ROLE_LABEL[role]}`}
        className={cn(
          'inline-flex items-center gap-1 rounded-full font-bold uppercase tracking-wider whitespace-nowrap font-mono',
          box,
          sz.box,
          className,
        )}
        {...rest}
      >
        <span
          className={cn('inline-flex items-center justify-center', iconClass)}
          aria-hidden="true"
        >
          <Icon size={sz.icon} color="currentColor" />
        </span>
        <span>{label ?? ADMIN_ROLE_LABEL[role]}</span>
      </span>
    );
  }

  // ── Team role path (F29) ──────────────────────────────────────────────
  const { box, showStar } = TEAM_ROLE_STYLE[role];
  return (
    <span
      role="img"
      aria-label={`Role: ${label ?? TEAM_ROLE_LABEL[role]}`}
      className={cn(
        'inline-flex items-center gap-1 rounded font-bold uppercase tracking-wider whitespace-nowrap font-mono',
        box,
        sz.box,
        className,
      )}
      {...rest}
    >
      {showStar ? (
        <span className="inline-flex items-center justify-center" aria-hidden="true">
          <StarIcon size={sz.icon} color="currentColor" />
        </span>
      ) : null}
      <span>{label ?? TEAM_ROLE_LABEL[role]}</span>
    </span>
  );
}

export default RoleBadge;
